import { useRef } from "react";

import { Link, useLocation, useNavigate } from "react-router-dom";

import { useFacilitatorContext } from "./FacilitatorContextProvider";

const validLevelSearch = (search) => {
  const params = new URLSearchParams(search || "");
  const levels = params.getAll("level");
  return levels.length === 1 && /^[1-9]\d*$/.test(levels[0])
    ? `?level=${levels[0]}`
    : "";
};

/**
 * THE ONE EXPLAINER, NAMED SO THREE CONTROLS CAN SHARE IT.
 *
 * The lock reaches both tabs here and the pace control in `FacilitatorCommandBand`,
 * and each of the three has to say WHY it is unavailable to a screen reader.
 * Three copies of one sentence would be three strings to keep in step and three
 * announcements of one fact; one line, referenced by `aria-describedby` from all
 * three, is the same information said once.
 */
export const NAV_LOCK_EXPLAINER_ID = "facilitator-nav-lock-explainer";
export const NAV_LOCK_EXPLAINER = "finish setting up your room to open these.";

export const FacilitatorDashboardTabs = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const shell = useFacilitatorContext();
  const refs = useRef([]);
  const isPrep = location.pathname === "/admin/prep";
  /* THE NAV IS DISABLED DURING SETUP, NOT HIDDEN (Amir's ruling, 2026-08-09).
     A facilitator part way through the setup rail could reach Prepare from this
     band and land on a surface with no room behind it; hiding the tabs would
     have fixed that and taught them nothing. Dimmed and inert, with one line
     saying why, keeps what is coming visible while the setup stays the only
     thing they can act on.

     THE SIGNAL IS THE SHELL'S, which is the same envelope Coach routes its own
     onboarding phase on, so the band and the panel underneath it cannot
     disagree about whether setup is finished. */
  const locked = shell?.onboardingRequired === true;
  // COACH IS FIRST (v2.2). It is the default landing route, so it is the first
  // pill: a tab band whose first entry is not where the teacher lands makes the
  // order describe something other than the journey. Prepare's `to` still
  // carries the level search only while already on Prep, unchanged.
  const tabs = [
    {
      key: "coach",
      label: "coach",
      to: "/admin/dashboard",
      panel: "facilitator-coach-panel",
      active: !isPrep,
    },
    {
      key: "prepare",
      label: "prepare",
      to: `/admin/prep${isPrep ? validLevelSearch(location.search) : ""}`,
      panel: "facilitator-prepare-panel",
      active: isPrep,
    },
  ];

  const handleKeyDown = (event, index) => {
    if (event.key === " " || event.key === "Spacebar") {
      event.preventDefault();
      navigate(tabs[index].to);
      return;
    }
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const offset = event.key === "ArrowRight" ? 1 : -1;
    const next = (index + offset + tabs.length) % tabs.length;
    refs.current[next]?.focus();
    navigate(tabs[next].to);
  };

  /* ARROWS STILL MOVE FOCUS WHILE LOCKED, AND MOVE NOTHING ELSE. A tablist
     whose arrow keys go dead is the defect `PrepSurface`'s level group was
     rewritten to avoid; a keyboard user has to be able to walk the band and
     hear both entries and the reason neither opens. */
  const handleLockedKeyDown = (event, index) => {
    if (event.key === " " || event.key === "Spacebar") {
      event.preventDefault();
      return;
    }
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const offset = event.key === "ArrowRight" ? 1 : -1;
    refs.current[(index + offset + tabs.length) % tabs.length]?.focus();
  };

  return (
    <div className="min-w-0">
      <div
        role="tablist"
        aria-label="dashboard mode"
        className="inline-flex rounded-full border border-white/[0.08] bg-background-blackpearl/70 p-1"
      >
        {tabs.map((tab, index) =>
          locked ? (
            /* A BUTTON, NOT A DIMMED LINK, and it carries no `disabled`
               attribute. A native `disabled` control leaves the tab order the
               instant it is set, so the one explanation a screen reader user
               has for why the product looks half open is the thing they can no
               longer reach. `aria-disabled` states the condition, the handlers
               enforce it, and `aria-describedby` carries the reason. This is
               the same treatment `Button`'s `blocked` prop applies, spelled out
               here because a tab is not a Button. */
            <button
              key={tab.key}
              type="button"
              ref={(node) => {
                refs.current[index] = node;
              }}
              id={`facilitator-${tab.key}-tab`}
              role="tab"
              aria-selected={tab.active}
              aria-controls={tab.panel}
              aria-disabled="true"
              aria-describedby={NAV_LOCK_EXPLAINER_ID}
              tabIndex={tab.active ? 0 : -1}
              onClick={(event) => event.preventDefault()}
              onKeyDown={(event) => handleLockedKeyDown(event, index)}
              className={[
                "focus-visible:focus-ring cursor-not-allowed rounded-full px-4 py-1.5 text-sm lowercase opacity-38 transition-all duration-fast",
                tab.active ? "bg-background-biscay text-lv0" : "text-lv3",
              ].join(" ")}
            >
              {tab.label}
            </button>
          ) : (
            <Link
              key={tab.key}
              ref={(node) => {
                refs.current[index] = node;
              }}
              id={`facilitator-${tab.key}-tab`}
              to={tab.to}
              role="tab"
              aria-selected={tab.active}
              aria-controls={tab.panel}
              tabIndex={tab.active ? 0 : -1}
              onKeyDown={(event) => handleKeyDown(event, index)}
              className={[
                "focus-visible:focus-ring rounded-full px-4 py-1.5 text-sm lowercase transition-all duration-fast",
                tab.active
                  ? "bg-background-biscay text-lv0 shadow-elevation-1"
                  : "text-lv3 hover:bg-state-hover hover:text-lv1",
              ].join(" ")}
            >
              {tab.label}
            </Link>
          ),
        )}
      </div>
      {locked ? (
        <p
          id={NAV_LOCK_EXPLAINER_ID}
          data-testid="facilitator-nav-lock-explainer"
          className="mt-1.5 text-xs text-lv4"
        >
          {NAV_LOCK_EXPLAINER}
        </p>
      ) : null}
    </div>
  );
};

export default FacilitatorDashboardTabs;
